import { observer } from 'mobx-react-lite';
import { ArrowForwardIconSvg, InfoIcon } from '../../../../lib/icons';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCreateBenefitSet } from '../hooks/useCreateBenefitSet';
import { Grid, Typography } from '@mui/material';
import { useStepper } from '../../partials/actionStepperPartial';
import { DatePicker, Select } from '../../../../lib/components';
import { useStore } from '../../../../hooks/useStore';
import Util from '../../../../misc/util';
import { format } from 'date-fns';
import { useActions } from '../../../../hooks/useActions';
import { EmployeeBenefitSetAssignmentTable } from '../../../../lib/components/tables/employee/employeeBenefitSetAssignment';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';

export const EmployeeAssignment = observer(() => {
  const { newBenefitSet } = useCreateBenefitSet();
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const { setNextButtonConfig, activeStep, setActiveStep, showInfoDrawer, setShowInfoDrawer } =
    useStepper();
  const { selectedEmployees } = useActions();
  const [benefitSetError, setBenefitSetError] = useState('');
  const [dateError, setDateError] = useState('');

  const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1, 1));
  const isEmployeeAssignment = location.pathname.includes('/assign_benefit_set_to_employees');

  const selectedEmployeeIds = useMemo(
    () => Object.keys(selectedEmployees ?? {}),
    [selectedEmployees]
  );

  const explanationText = isEmployeeAssignment
    ? t('benefitSet:chooseStartDate')
    : t('benefitSet:chooseBenefitSetAndStartDate');

  useEffect(() => {
    setNextButtonConfig({
      text: t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      disabled: selectedEmployeeIds.length === 0,
      onClick: () => {
        if (!newBenefitSet.editingBenefitSetId)
          setBenefitSetError(t('benefitSet:errorChooseBenefitSet'));
        else if (newBenefitSet.startMonth === null || newBenefitSet.startMonth === undefined) {
          setDateError(t('benefitSet:errorChooseStartDate'));
        } else {
          setActiveStep(activeStep + 1);
        }
      }
    });
  }, [selectedEmployeeIds, newBenefitSet.editingBenefitSetId, newBenefitSet.startMonth]);

  useEffect(() => {
    companyStore.fetchBenefitSets();
  }, [companyStore.company?.id]);

  const getBenefitSetOptions = (locale: Locale) => {
    if (!companyStore.benefitSets) return [];
    return companyStore.benefitSets.map((benefitSet) => {
      const nameKey = locale.code === 'de' ? 'name_de' : 'name_en';
      const fallbackName = benefitSet.name ?? `Benefit Set ${benefitSet.id}`;

      return {
        value: benefitSet.id,
        text: benefitSet[nameKey] ?? fallbackName
      };
    });
  };

  return (
    <>
      <Grid container direction="row" md={12}>
        <Grid container alignItems="center">
          <Grid item marginTop={-2} marginBottom={-2} className="text-midnight-main">
            {explanationText}
          </Grid>
        </Grid>

        <Grid container md={12} spacing={3} marginTop={0.5}>
          <Grid item xs={12} md={12}>
            <Grid container marginBottom={2} spacing={3}>
              <Grid item md={6}>
                <Select
                  label={t('common:BENEFIT_SET')}
                  name={'benefit_set'}
                  value={newBenefitSet.editingBenefitSetId}
                  onChange={(e) => {
                    setBenefitSetError('');
                    newBenefitSet.editingBenefitSetId = e.target.value;
                  }}
                  error={!!benefitSetError}
                  helperText={benefitSetError}
                  options={getBenefitSetOptions(Util.getLocale())}
                  isMultiple={false}
                  placeholder={t('benefitSet:chooseBenefitSet')}
                  disabled={selectedEmployeeIds.length === 0 || isEmployeeAssignment}
                />
              </Grid>
              <Grid item md={6}>
                <DatePicker
                  variant="month"
                  label={t('benefitSet:assignmentMonth')}
                  onChange={(dateStr) => {
                    newBenefitSet.startMonth = new Date(dateStr).getMonth();
                    newBenefitSet.startYear = new Date(dateStr).getFullYear();
                  }}
                  onChangeInvalidDate={() => setDateError(t('form:INVALID_DATE'))}
                  helperText={dateError}
                  error={!!dateError}
                  placeholder={t('benefitSet:selectAssignmentMonth')}
                  initialValue={
                    newBenefitSet.startYear && newBenefitSet.startMonth !== undefined
                      ? format(
                          new Date(newBenefitSet.startYear, newBenefitSet.startMonth, 1),
                          'yyyy-MM-dd'
                        )
                      : undefined
                  }
                  minDate={nextMonth}
                  disablePast={true}
                  disabled={selectedEmployeeIds.length === 0}
                />
              </Grid>
            </Grid>

            <div style={{ marginTop: '24px' }}>
              <EmployeeBenefitSetAssignmentTable />
            </div>
          </Grid>
        </Grid>
      </Grid>
      <Drawer
        label={t('benefitSet:importantNotes')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('benefitSet:assignmentInfoDrawer:headline1')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'benefitSet:assignmentInfoDrawer:text1'}
              components={[
                <a
                  key="link"
                  href="https://www.gesetze-im-internet.de/agg/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="open link"
                />
              ]}
            />
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'benefitSet:assignmentInfoDrawer:text2'}
              components={{ bold: <strong /> }}
            />
          </Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('benefitSet:assignmentInfoDrawer:headline2')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'benefitSet:assignmentInfoDrawer:text3'}
              components={[
                <a
                  key="link"
                  href="https://support.pxtra.de/platform/kann-ich-meinen-mitarbeitenden-bereits-genutzte-benefits-wieder-entziehen"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="open link"
                />
              ]}
            />
          </Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('benefitSet:assignmentInfoDrawer:headline3')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'benefitSet:assignmentInfoDrawer:text4'}
              components={{ bold: <strong /> }}
            />
          </Typography>

          <Typography
            paragraph
            style={{
              fontSize: 'small',
              color: 'grey.600',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}
          >
            <div className="flex text-xs text-grey-600 gap-x-2">
              <InfoIcon className="h-4 w-4" />
              {t('benefitSet:assignmentInfoDrawer:legalNote')}
            </div>
          </Typography>
        </>
      </Drawer>
    </>
  );
});
